import React, { Suspense } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2, BookText, ExternalLink } from 'lucide-react';

// Lazy load the PDFReader component for code splitting
const PDFReader = React.lazy(() => import('./PDFReader'));

interface LazyPDFReaderProps {
  url: string;
  title: string;
  onSwitchToText?: () => void;
}

const PDFLoadingFallback: React.FC<{ 
  onSwitchToText?: () => void;
  onOpenInNewTab?: () => void;
  title: string;
}> = ({ onSwitchToText, onOpenInNewTab, title }) => {
  return (
    <Card className="w-full max-w-4xl mx-auto">
      <CardContent className="p-8 text-center">
        <Loader2 className="h-16 w-16 animate-spin mx-auto mb-4" />
        <h3 className="text-lg font-semibold mb-2">Loading PDF Reader...</h3>
        <p className="text-muted-foreground mb-6">
          Preparing the PDF reader for "{title}". This might take a moment.
        </p>
        
        {/* Progressive Enhancement - Show alternatives during loading */}
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          {onSwitchToText && (
            <Button variant="outline" onClick={onSwitchToText} className="flex items-center gap-2">
              <BookText className="h-4 w-4" />
              Switch to Text Reader (Faster)
            </Button>
          )}
          {onOpenInNewTab && (
            <Button variant="outline" onClick={onOpenInNewTab} className="flex items-center gap-2">
              <ExternalLink className="h-4 w-4" />
              Open PDF in New Tab
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

const LazyPDFReader: React.FC<LazyPDFReaderProps> = ({ url, title, onSwitchToText }) => {
  const handleOpenInNewTab = () => {
    window.open(url, '_blank');
  };

  return (
    <Suspense 
      fallback={
        <PDFLoadingFallback 
          onSwitchToText={onSwitchToText}
          onOpenInNewTab={handleOpenInNewTab}
          title={title}
        />
      }
    >
      <PDFReader 
        url={url} 
        title={title} 
        onSwitchToText={onSwitchToText}
      />
    </Suspense>
  );
};

export default LazyPDFReader;